import { Bookmark, PlayCircle, MessageCircle, Layers, ExternalLink, Siren } from "lucide-react";
import { useState } from "react";
import { useNewsStore } from "../../lib/newsStore";
import { safeUrl } from "../../lib/safeUrl";
import { timeAgo } from "../../lib/tone";
import { MODEL_LABELS, PERSPECTIVE_COLORS, formatDate } from "../../lib/newsApi";

export const SideChip = ({ side }) => (
  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${side === "independent" ? "bg-emerald-50 text-emerald-800" : "bg-sky-50 text-sky-800"}`}>{side}</span>
);

export const PerspectiveChip = ({ value }) => value ? (
  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" title="Approximate editorial perspective (public media-rating sources)">
    <span className="h-2 w-2 rounded-full" style={{ background: PERSPECTIVE_COLORS[value] }} />{value}
  </span>
) : null;

const KindIcon = ({ kind }) => kind === "video" ? <PlayCircle size={14} className="text-rose-500" /> : kind === "social" ? <MessageCircle size={14} className="text-sky-500" /> : null;

export const ReadLaterButton = ({ item }) => {
  const { isLater, toggleLater } = useNewsStore();
  const on = isLater(item.id);
  return (
    <button onClick={() => toggleLater(item)} aria-pressed={on} aria-label={on ? "Remove from read later" : "Read later"} data-testid={`read-later-${item.id}`}
      className={`rounded-full p-1.5 transition-colors ${on ? "bg-amber-50 text-amber-600" : "text-slate-400 hover:text-amber-600"}`}>
      <Bookmark size={16} fill={on ? "currentColor" : "none"} />
    </button>
  );
};

export const NewsCard = ({ item, onOpenStory }) => {
  const [showAlso, setShowAlso] = useState(false);
  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md sm:p-5" data-testid={`news-card-${item.id}`}>
      <div className="flex items-start gap-2 text-xs text-slate-500">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <KindIcon kind={item.kind} />
          <span className="font-semibold text-slate-800" data-testid="news-card-source">{item.outlet || item.source}</span>
          <SideChip side={item.source_type} />
          <span className="rounded-md border border-slate-200 px-1.5 py-0.5 text-[10px]" title={item.owner}>{MODEL_LABELS[item.ownership_model]}</span>
          <PerspectiveChip value={item.perspective} />
        </div>
        <span className="flex shrink-0 items-center gap-1">
          <span className="whitespace-nowrap" title={formatDate(item.published)} data-testid="news-card-date">{timeAgo(item.published)}</span>
          <ReadLaterButton item={item} />
        </span>
      </div>
      <a href={safeUrl(item.link)} target="_blank" rel="noopener noreferrer" className="group mt-2" data-testid={`news-card-link-${item.id}`}>
        <h3 className="font-medium leading-snug text-slate-900 group-hover:text-sky-900">{item.title} <ExternalLink size={12} className="ml-0.5 inline text-slate-400" /></h3>
      </a>
      {item.summary && <p className="mt-1.5 line-clamp-2 text-sm text-slate-600">{item.summary}</p>}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {item.alerts?.map((a) => <span key={a} className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-0.5 text-[11px] font-medium capitalize text-rose-700"><Siren size={11} />{a}</span>)}
        {item.topics?.filter((t) => !item.alerts?.includes(t.toLowerCase())).map((t) => <span key={t} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{t}</span>)}
        {item.via && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">via {item.via}</span>}
      </div>
      <div className="mt-auto flex flex-wrap items-center gap-3 pt-3 text-xs">
        {item.cluster && (
          <button onClick={() => onOpenStory?.(item.cluster.id)} className="inline-flex items-center gap-1 font-medium text-sky-800 hover:underline" data-testid={`news-card-story-${item.id}`}>
            <Layers size={13} />{item.cluster.source_count} outlets · {item.cluster.coverage === "both" ? "both sides" : item.cluster.coverage?.replace("_", " ")} — compare
          </button>
        )}
        {item.also?.length > 0 && (
          <button onClick={() => setShowAlso((s) => !s)} className="text-slate-500 hover:text-slate-800" data-testid={`news-card-also-${item.id}`}>
            {showAlso ? "Hide" : `+${item.also.length} similar`}
          </button>
        )}
      </div>
      {showAlso && (
        <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2 text-xs">
          {item.also.map((a) => (
            <li key={a.link}><a href={safeUrl(a.link)} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-sky-900"><span className="font-medium">{a.outlet}</span> · {a.title}</a></li>
          ))}
        </ul>
      )}
    </article>
  );
};
